import type { FetcherWithComponents } from '@remix-run/react';
import type {
    CopyDesignBoardPinsMutation,
    MoveDesignBoardPinsMutation,
} from 'generated/graphql';
import { DesignBoardDrawerSelectPinsListItem } from './DesignBoardDrawerSelectPinsListItem';
import { useEffect, useState } from 'react';
import type { PinMoveData } from '~/modules/design-board/type/main-context-types';
import type {
    CopyToBoardClickedProps,
    MoveToBoardClickedProps,
} from './DesignBoardFetchers';
import type { OnSucessProps } from '../../hook/use-pin-move-copy-on-sucess-toast';
import SearchDesignBoardList from '~/modules/design-board/component/right-side-drawer/SearchDesignBoardList';
import type { BoardListItem } from '~/modules/design-board/type';
import { fetcherIsDone } from '~/utils/fetcherHelper';

export type PinFetcherFunction = (
    props: MoveToBoardClickedProps | CopyToBoardClickedProps
) => void;

export type ActionTakenType = 'moved' | 'copied' | 'saved';
interface DesignBoardDrawerSelectPinsBodyProps {
    pinData: PinMoveData;
    onError: (errorMessage: string) => void;
    onSuccess: (onSucessProps: OnSucessProps) => void;
    pinFetcher: FetcherWithComponents<
        MoveDesignBoardPinsMutation | CopyDesignBoardPinsMutation
    >;
    pinFetcherFunction: PinFetcherFunction;
    inputLabel: string;
    listButtonLabel: string;
    actionTaken: ActionTakenType;
}

const DesignBoardDrawerSelectPinsBody = ({
    pinData: pinMoveData,
    onError,
    onSuccess,
    pinFetcher,
    actionTaken,
    pinFetcherFunction,
    inputLabel,
    listButtonLabel,
}: DesignBoardDrawerSelectPinsBodyProps) => {
    const { pinListString, boardId } = pinMoveData;

    const [clickedId, setClickedId] = useState<number | null>(null);
    const [boardSelected, setBoardSelected] = useState<BoardListItem>();

    const onSavePin = (
        destinationBoardId: number,
        boardItemData: BoardListItem
    ) => {
        setClickedId(destinationBoardId);
        const props = {
            destinationBoardId,
            boardId,
            pinListString,
            onError,
            pinFetcher,
        } as MoveToBoardClickedProps | CopyToBoardClickedProps;
        setBoardSelected(boardItemData);
        pinFetcherFunction(props);
    };

    const virtualListItemFn = (data: BoardListItem) => {
        return data.id !== boardId ? (
            <DesignBoardDrawerSelectPinsListItem
                clickedId={clickedId}
                key={`boardListItem${data?.id ?? ''}`}
                label={listButtonLabel}
                {...data}
                onSave={(boardId) => onSavePin(boardId, data)}
            />
        ) : (
            <></>
        );
    };

    useEffect(() => {
        if (fetcherIsDone(pinFetcher)) {
            const boardName = boardSelected?.boardName || '';
            const urlSegment = boardSelected?.urlSegment || '';
            const pinCount = pinListString.split(',').length;
            onSuccess({ actionTaken, boardName, pinCount, urlSegment });
        }
    }, [
        onSuccess,
        pinFetcher,
        pinListString,
        actionTaken,
        clickedId,
        boardSelected,
    ]);

    return (
        <SearchDesignBoardList
            headerLabel={inputLabel}
            virtualListItemFn={virtualListItemFn}
        />
    );
};

export default DesignBoardDrawerSelectPinsBody;
